import { Edit } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import OutboxIcon from "@mui/icons-material/Outbox";
import PersonIcon from "@mui/icons-material/Person";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
	DataGridPremium,
	type GridCellParams,
	type GridColDef,
} from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { PaginationModel } from "../types/UserTypes";
import type { User } from "@repo/server-api";

export type LoginsDataGridProps = {
	rows: User[];
	rowCount: number;
	isLoading: boolean;
	paginationModel: PaginationModel;
	onPaginationModelChange: (model: PaginationModel) => void;
	onEditUser: (user: User) => void;
	onDeleteUser: (userId: string) => void;
	onImpersonateUser: (user: User) => void;
	onResendVerificationEmail: (user: User) => void;
};

export const LoginsDataGrid = ({
	rows,
	rowCount,
	isLoading,
	paginationModel,
	onPaginationModelChange,
	onEditUser,
	onDeleteUser,
	onImpersonateUser,
	onResendVerificationEmail,
}: LoginsDataGridProps) => {
	const { t } = useTranslation();

	const columns: GridColDef<User>[] = useMemo(
		() => [
			{
				field: "name",
				headerName: t("name"),
				width: 150,
				renderCell(params: GridCellParams<User>) {
					return `${params.row.firstName} ${params.row.lastName}`;
				},
			},
			{
				field: "username",
				headerName: t("username"),
				width: 150,
			},
			{
				field: "groups",
				headerName: t("groups"),
				width: 200,
				renderCell(params: GridCellParams<User>) {
					return params.row.groups.map((g: string) => t(`groups.${g}`)).join(", ");
				},
			},
			{
				field: "email",
				headerName: t("email"),
				flex: 1,
			},
			{
				field: "emailVerified",
				headerName: t("email-verified"),
				align: "center",
				headerAlign: "center",
				width: 150,
				renderCell(params: GridCellParams<User>) {
					return params.row.emailVerified ? t("yes") : t("no");
				},
			},
			{
				field: "enabled",
				headerName: t("login-enabled"),
				align: "center",
				headerAlign: "center",
				width: 150,
				renderCell(params: GridCellParams<User>) {
					return params.row.enabled ? t("yes") : t("no");
				},
			},
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				align: "right",
				headerAlign: "center",
				width: 200,
				renderCell: (params: GridCellParams<User>) => (
					<Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", alignItems: "center", height: "100%" }}>
						{!params.row.emailVerified && (
							<Tooltip title={t("resend-verification-email")}>
								<IconButton
									onClick={() => onResendVerificationEmail(params.row)}
									data-testid="resend-email-button"
									size="small"
								>
									<OutboxIcon />
								</IconButton>
							</Tooltip>
						)}
						<Tooltip title={t("impersonate-user")}>
							<IconButton
								onClick={() => onImpersonateUser(params.row)}
								data-testid="impersonate-button"
								size="small"
							>
								<PersonIcon />
							</IconButton>
						</Tooltip>
						<Tooltip title={t("edit")}>
							<IconButton
								onClick={() => onEditUser(params.row)}
								data-testid="edit-button"
								size="small"
							>
								<Edit />
							</IconButton>
						</Tooltip>
						<Tooltip title={t("delete")}>
							<IconButton
								onClick={() => onDeleteUser(params.row.id)}
								data-testid="delete-button"
								size="small"
							>
								<DeleteIcon />
							</IconButton>
						</Tooltip>
					</Box>
				),
			},
		],
		[t, onResendVerificationEmail, onImpersonateUser, onEditUser, onDeleteUser],
	);

	return (
		<Box data-testid="logins-data-grid">
			<DataGridPremium
				rows={rows}
				columns={columns}
				rowCount={rowCount}
				pagination
				paginationMode="server"
				paginationModel={paginationModel}
				onPaginationModelChange={onPaginationModelChange}
				pageSizeOptions={[10, 25, 50, 100]}
				loading={isLoading}
				disableRowSelectionOnClick
				localeText={{
					MuiTablePagination: {
						labelRowsPerPage: t("pagination.rows-per-page", { ns: "common" }),
					},
				} as never}
				sx={{
					border: "none",
					backgroundColor: "transparent",
				}}
			/>
		</Box>
	);
};
